import React, { useState, useEffect } from 'react';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { Popup } from 'clad-ui';
import LoginForm, { type LoginInputs } from '~/components/login/LoginForm';
import VerifyOTPPopup from '~/components/login/VerifyOTPPopup';
import SocialGroup from '~/components/common/SocialGroup';
import BanPopup from '~/components/common/BanPopup';
import IDLayout from '~/components/common/IDLayout';
import BoxWithLogo from '~/components/common/BoxWithLogo';
import { useLogin, useAuthorize, authozieOnServer } from '~/apis/login';
import { useGetBanStatus } from '~/apis/uba';
import { ERR_CODE } from '~/helpers/constants';
import { setCookiesByPhone, setCookiesBySocial } from '~/helpers/cookies';
import { removeCookie } from '~/helpers/api/Cookie';
import { gtmCustomDemesion, genderToGA4Data, asyncGTMPageMetadata, gtmEventClick } from '~/helpers/tagging/gtm';
import fingerprintV2 from '~/helpers/fingerprintV2';
import { checkIsMobile } from '~/helpers/util';
import { referrerDomain } from '~/config';
import fingerprint from '~/helpers/fingerprint';
import EncourageRegister from '~/components/login/EncourageRegister';
const windowRedirect = (url: string) => {
  window.location.href = url;
};
const Login = ({
  isMobile
}: {
  isMobile: boolean;
}) => {
  const {
    query
  } = useRouter();
  const continueUrl = query.continue?.toString() || '';
  const eventSource = query.event_source?.toString() || '';
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [announcer, setAnnouncer] = useState({
    open: false,
    message: ''
  });
  const [popup, setPopup] = useState({
    open: false,
    header: '',
    body: '',
    buttonText: ''
  });
  const [banPopup, setBanPopup] = useState({
    open: false,
    data: null
  });
  const [verifyOTPPopup, setVerifyOTPPopup] = useState({
    open: false,
    data: null
  });
  const {
    loading: loginLoading,
    fetch: login
  } = useLogin();
  const {
    loading: authorizeLoading,
    fetch: authorize
  } = useAuthorize();
  const {
    fetch: getBanStatus
  } = useGetBanStatus();
  const isReferrerDomain = referrerDomain.some((elm: any) => (query.continue || '').indexOf(elm) > -1);
  const isKeepQuery = query.service === 'flashad' || isReferrerDomain;
  const phoneQuery = isKeepQuery && query.phone || '';
  const defaultPhone = Array.isArray(phoneQuery) ? phoneQuery[0] : phoneQuery;
  useEffect(() => {
    asyncGTMPageMetadata({
      page: 'login',
      rest: {
        identifier_category: 'non-logged_in',
        event_source: eventSource
      }
    });
  }, []);
  const onLoginWithFingerprint = async (data: LoginInputs, fingerprintV2: string) => {
    try {
      const {
        phone,
        password
      } = data;
      const loginChallenge = await authorize({
        continueUrl,
        fingerprintV2
      });
      if (loginChallenge) {
        const id = await fingerprint.get();
        const {
          data,
          error
        } = await login({
          phone: phone.trim(),
          password,
          loginChallenge,
          fingerprint: id,
          fingerprintV2
        });
        if (data) {
          setCookiesByPhone(data.data);
          // default menu mode is user
          removeCookie('currentMenuMode');
          handleTaggingAfterLogin(data.data);
          windowRedirect(data.data.redirect_to);
        } else {
          handleErrorByPhone(error);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const onLogin = async (data: LoginInputs) => {
    setIsLoading(true);
    const {
      phone
    } = data;
    setPhone(phone);
    gtmEventClick({
      category: 'login',
      label: 'login_button'
    });
    setAnnouncer({
      open: false,
      message: ''
    });
    const idV2 = fingerprintV2.getDeviceFingerprint();
    if (idV2) {
      onLoginWithFingerprint(data, idV2);
    } else {
      fingerprintV2.pushCallback(id => onLoginWithFingerprint(data, id));
    }
  };
  const onSocialLoggedIn = (data: any, error: any) => {
    if (data && data.success) {
      setCookiesBySocial(data.data);
      windowRedirect(data.data.redirect_to);
    } else {
      handleError(error);
    }
  };
  const handleTaggingAfterLogin = async (result: any) => {
    gtmCustomDemesion({
      event: 'login',
      rest: {
        method: 'phoneNumber',
        user_profile_gender: genderToGA4Data(result.profile.gender),
        chotot_id: result.profile.account_id,
        logged_in_status: 'yes'
      }
    });
  };
  const handleErrorByPhone = async (error: any) => {
    const errorData = error.error_data;
    if (errorData?.err_code === ERR_CODE.LOGIN && errorData?.err_type === 'mfa') {
      setVerifyOTPPopup({
        open: true,
        data: errorData
      });
    } else if (errorData?.err_code === ERR_CODE.LOGIN && errorData?.modal_header) {
      asyncGTMPageMetadata({
        page: 'account_is_blocked'
      });
      setPopup({
        open: true,
        header: errorData?.modal_header,
        body: errorData?.modal_body,
        buttonText: errorData?.modal_button_text
      });
    } else if (errorData?.err_code === ERR_CODE.TERMINATED) {
      if (errorData?.ext?.account_id) {
        const {
          data
        } = await getBanStatus(errorData?.ext.account_id);
        if (data?.success) {
          const banInfo = data.data.accounts[0];
          setBanPopup({
            open: true,
            data: banInfo
          });
        }
      }
    } else {
      setAnnouncer({
        open: true,
        message: error.error_message || errorData?.message || 'Đã có lỗi xảy ra, vui lòng thử lại sau.'
      });
    }
  };
  const handleError = async (error: any) => {
    if (error.err_code === ERR_CODE.LOGIN && error.modal_header) {
      asyncGTMPageMetadata({
        page: 'account_is_blocked'
      });
      setPopup({
        open: true,
        header: error.modal_header,
        body: error.modal_body,
        buttonText: error.modal_button_text
      });
    } else if (error.err_code === ERR_CODE.TERMINATED) {
      if (error.ext?.account_id) {
        const {
          data
        } = await getBanStatus(error.ext.account_id);
        if (data?.success) {
          const banInfo = data.data.accounts[0];
          setBanPopup({
            open: true,
            data: banInfo
          });
        }
      }
    } else {
      setAnnouncer({
        open: true,
        message: error.message || error.error_message || 'Đã có lỗi xảy ra, vui lòng thử lại sau.'
      });
    }
  };
  const goRegister = () => {
    gtmEventClick({
      category: 'login',
      label: 'register_button'
    });
  };
  const goForgotPassword = () => {
    gtmEventClick({
      event: 'reset_password',
      category: 'reset_password',
      action: 'click_forgot_password'
    });
  };
  const onInvalid = () => {
    setAnnouncer({
      open: false,
      message: ''
    });
  };

  // clear fingerprintV2 callbacks after 10 seconds
  useEffect(() => {
    setTimeout(() => {
      if (!isLoading || fingerprintV2.getDeviceFingerprint()) return;
      fingerprintV2.clearCallbacks();
      setIsLoading(false);
      setAnnouncer({
        open: true,
        message: 'Đã có lỗi xảy ra, vui lòng thử lại sau.'
      });
    }, 10000);
  }, [isLoading]);
  return <IDLayout isMobile={isMobile}>
      <BoxWithLogo>
        <EncourageRegister goRegister={goRegister} continueUrl={continueUrl} />

        <BoxWithLogo.Title>Đăng nhập</BoxWithLogo.Title>
        <BoxWithLogo.ErrorArea open={announcer.open} message={announcer.message} />

        <LoginForm onSubmit={onLogin} isLoading={isLoading || loginLoading || authorizeLoading} defaultPhone={defaultPhone} goForgotPassword={goForgotPassword} onInvalid={onInvalid} />

        <SocialGroup title="Hoặc đăng nhập bằng" onLoggedIn={onSocialLoggedIn} />

        <p className={"pnv45ui"}>
          Chưa có tài khoản?{' '}
          <a onClick={goRegister} href={`/register?continue=${continueUrl}`}>
            Đăng ký tài khoản mới
          </a>
        </p>
      </BoxWithLogo>

      <Popup open={popup.open} btnRowPosition="justify" haveClose={false} title={popup.header} primaryBtnLabel={popup.buttonText} onClose={() => setPopup({
      ...popup,
      open: false
    })} onPrimaryButtonClick={() => setPopup({
      ...popup,
      open: false
    })}>
        <div dangerouslySetInnerHTML={{
        __html: popup.body
      }} />
      </Popup>
      <BanPopup open={banPopup.open} data={banPopup.data} onClose={() => setBanPopup({
      ...banPopup,
      open: false
    })} />
      <VerifyOTPPopup open={verifyOTPPopup.open} phone={phone} data={verifyOTPPopup.data} onClose={() => setVerifyOTPPopup({
      ...verifyOTPPopup,
      open: false
    })} />
    </IDLayout>;
};
export const getServerSideProps: GetServerSideProps = async ({
  req: {
    headers,
    cookies
  },
  query
}) => {
  try {
    // skip check authorize on development
    if (process.env.NODE_ENV !== 'development') {
      const authorize = await authozieOnServer(query.continue, cookies, {
        origin: headers.origin || headers[':authority'] ? `https://${headers[':authority']}` : headers.host ? `https://${headers.host}` : '',
        referer: headers.referer
      });
      if (authorize.success && authorize.data.skip_authorize) {
        return {
          redirect: {
            destination: authorize.data.redirect_to,
            permanent: false
          }
        };
      }
    }
  } catch (err) {
    console.error(err);
  }
  const isMobile = checkIsMobile(headers['user-agent'] || '');
  return {
    props: {
      isMobile
    }
  };
};
export default Login;

require("./index.linaria.module.css");